<template>
  <div class="employee-survey-responses">
    <!-- Statistics Section -->
    <b-card class="mb-4" v-if="statistics">
      <b-card-header>
        <h4 class="mb-0">Survey Statistics</h4>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col md="2" class="text-center">
            <div class="stat-card">
              <h3 class="text-primary">{{ statistics.totalResponses }}</h3>
              <p class="mb-0">Total Responses</p>
            </div>
          </b-col>
          <b-col md="2" class="text-center">
            <div class="stat-card">
              <h3 class="text-success">{{ statistics.submittedResponses }}</h3>
              <p class="mb-0">Submitted</p>
            </div>
          </b-col>
          <b-col md="2" class="text-center">
            <div class="stat-card">
              <h3 class="text-warning">{{ statistics.draftResponses }}</h3>
              <p class="mb-0">Drafts</p>
            </div>
          </b-col>
          <b-col md="2" class="text-center">
            <div class="stat-card">
              <h3 class="text-info">{{ statistics.anonymousResponses }}</h3>
              <p class="mb-0">Anonymous</p>
            </div>
          </b-col>
          <b-col md="2" class="text-center">
            <div class="stat-card">
              <h3 class="text-secondary">{{ statistics.completionRate }}%</h3>
              <p class="mb-0">Completion Rate</p>
            </div>
          </b-col>
          <b-col md="2" class="text-center">
            <div class="stat-card">
              <h3 class="text-dark">{{ statistics.recentResponses }}</h3>
              <p class="mb-0">Last 7 Days</p>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card class="mb-4">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h2 class="mb-0">Employee Survey Responses</h2>
        <b-button variant="success" @click="exportToExcel" size="sm">
          Export to Excel
        </b-button>
      </b-card-header>
      <b-card-body>
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
          <p class="mt-2">Loading responses...</p>
        </div>
        
        <!-- Filters -->
        <b-row class="mb-3" v-if="!loading">
          <b-col md="3">
            <b-form-group label="Status">
              <b-form-select v-model="filters.status" @change="onFilterChange">
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Anonymous">
              <b-form-select v-model="filters.isAnonymous" @change="onFilterChange">
                <option value="">All</option>
                <option value="true">Anonymous</option>
                <option value="false">Named</option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Date From">
              <b-form-input type="date" v-model="filters.dateFrom" @change="onFilterChange"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Date To">
              <b-form-input type="date" v-model="filters.dateTo" @change="onFilterChange"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        
        <b-table
          v-if="!loading"
          ref="table"
          :items="responses"
          :fields="fields"
          :primary-key="'id'"
          responsive
          hover
          :empty-text="'No responses found.'"
          :details-visible="detailsVisible"
          @row-toggle-details="onRowToggleDetails"
        >
          <template #cell(empId)="{ item }">
            <b-badge variant="info">{{ item.empId }}</b-badge>
          </template>
          <template #cell(status)="{ item }">
            <b-badge :variant="getStatusVariant(item.status)">{{ item.status }}</b-badge>
          </template>
          <template #cell(id)="{ item }">
            {{ item.id }}
          </template>
          <template #cell(submissionDateTime)="{ item }">
            {{ item.submissionDateTime }}
          </template>
          <template #cell(actions)="{ item }">
            <div class="d-flex gap-1">
              <b-button size="sm" @click="toggleDetails(item.id)">
                {{ detailsVisible.includes(item.id) ? 'Hide' : 'View' }} Details
              </b-button>
              <b-button size="sm" variant="danger" @click="deleteResponse(item.id)">
                Delete
              </b-button>
            </div>
          </template>
          <template #row-details="{ item }">
            <b-card class="mb-2">
              <div v-for="section in Object.values(item.responses)" :key="section.section" class="mb-3">
                <h5>{{ section.section }}</h5>
                <b-table
                  :items="sectionTable(section.responses)"
                  :fields="sectionFields(section.section)"
                  small
                  responsive
                  class="mb-2"
                >
                  <template v-if="section.section !== 'Final Feedback'" #cell(question)="{ value }">
                    <span>{{ value }}</span>
                  </template>
                  <template v-if="section.section !== 'Final Feedback'" #cell(answer)="{ value }">
                    <b-badge variant="primary">{{ value }}</b-badge>
                  </template>
                  <template v-if="section.section !== 'Final Feedback'" #cell(remark)="{ value }">
                    <span>{{ value }}</span>
                  </template>
                  <template v-if="section.section === 'Final Feedback'" #cell(key)="{ value }">
                    <b>{{ value }}</b>
                  </template>
                  <template v-if="section.section === 'Final Feedback'" #cell(value)="{ value }">
                    <span>{{ value }}</span>
                  </template>
                </b-table>
              </div>
            </b-card>
          </template>
        </b-table>
        <!-- Pagination -->
        <b-pagination
          v-if="!loading"
          v-model="pagination.page"
          :total-rows="pagination.total"
          :per-page="pagination.limit"
          :total-pages="pagination.totalPages"
          align="center"
          class="mt-3"
          @change="onPageChange"
        />
        
        <!-- Response Count -->
        <div v-if="!loading" class="text-center mt-2">
          <small class="text-muted">
            Showing {{ responses.length }} of {{ pagination.total }} responses
          </small>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import EmployeeSurveyService from '@/services/employee-survey-service'
import { 
  BSpinner, 
  BCard, 
  BCardHeader, 
  BCardBody, 
  BRow, 
  BCol, 
  BButton, 
  BTable, 
  BBadge, 
  BFormGroup, 
  BFormSelect, 
  BFormInput, 
  BPagination 
} from 'bootstrap-vue'

export default {
  name: 'EmployeeSurveyResponses',
  data() {
    return {
      responses: [],
      fields: [
        { key: 'empId', label: 'Employee ID' },
        { key: 'status', label: 'Status' },
        { key: 'id', label: 'Response ID' },
        { key: 'submissionDateTime', label: 'Submission Date & Time' },
        { key: 'actions', label: 'Actions', class: 'text-center' },
      ],
      currentPage: 1,
      detailsVisible: [],
      loading: false,
      statistics: null,
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      },
      filters: {
        status: '',
        isAnonymous: '',
        dateFrom: '',
        dateTo: ''
      }
    }
  },
  methods: {
    getStatusVariant(status) {
      const variants = {
        'Not Initiated': 'secondary',
        'Initiated': 'info',
        'Draft': 'warning',
        'Submitted': 'success',
      }
      return variants[status] || 'secondary'
    },
    toggleDetails(id) {
      if (this.detailsVisible.includes(id)) {
        this.detailsVisible = this.detailsVisible.filter(i => i !== id)
        console.log('Hiding details for row:', id, 'Current detailsVisible:', this.detailsVisible)
      } else {
        this.detailsVisible = [id] // Only one open at a time
        console.log('Showing details for row:', id, 'Current detailsVisible:', this.detailsVisible)
      }
    },
    onRowToggleDetails(visible, id) {
      if (visible) {
        this.detailsVisible = [id]
        console.log('Row details opened for:', id, 'Current detailsVisible:', this.detailsVisible)
      } else {
        this.detailsVisible = this.detailsVisible.filter(i => i !== id)
        console.log('Row details closed for:', id, 'Current detailsVisible:', this.detailsVisible)
      }
    },
    sectionTable(sectionResponses) {
      // For Final Feedback, show as key-value
      if (typeof sectionResponses === 'object' && !Array.isArray(sectionResponses) && Object.keys(sectionResponses).some(k => typeof sectionResponses[k] === 'string')) {
        return Object.keys(sectionResponses).map(key => ({ key, value: sectionResponses[key] }))
      }
      // For normal sections
      return Object.values(sectionResponses)
    },
    sectionFields(sectionKey) {
      if (sectionKey === 'Final Feedback') {
        return [
          { key: 'key', label: 'Question' },
          { key: 'value', label: 'Response' },
        ]
      }
      return [
        { key: 'question', label: 'Question' },
        { key: 'answer', label: 'Answer' },
        { key: 'remark', label: 'Remark' },
      ]
    },
    async loadResponses() {
      try {
        this.loading = true
        
        // Build query parameters
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit
        }
        
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.isAnonymous !== '') params.isAnonymous = this.filters.isAnonymous
        if (this.filters.dateFrom) params.dateFrom = this.filters.dateFrom
        if (this.filters.dateTo) params.dateTo = this.filters.dateTo
        
        const response = await EmployeeSurveyService.getAllResponses(params)
        
        if (response.success && response.data) {
          this.responses = response.data.responses.map((resp, idx) => ({
            ...resp,
            id: resp.id || idx + 1,
            empId: resp.emp_id || resp.empId || 'Anonymous',
            submissionDateTime: resp.created_at ? new Date(resp.created_at).toLocaleString() : ''
          }))
          
          this.pagination = {
            page: response.data.pagination.page,
            limit: response.data.pagination.limit,
            total: response.data.pagination.total,
            totalPages: response.data.pagination.totalPages
          }
        }
      } catch (error) {
        console.error('Error loading responses:', error)
        this.$bvToast.toast(error.message || 'Failed to load responses', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
        
        // Fallback to localStorage if API fails
        this.loadResponsesFromLocalStorage()
      } finally {
        this.loading = false
      }
    },
    
    async loadStatistics() {
      try {
        const response = await EmployeeSurveyService.getResponseStatistics()
        if (response.success && response.data.statistics) {
          this.statistics = response.data.statistics
        }
      } catch (error) {
        console.error('Error loading statistics:', error)
      }
    },
    
    loadResponsesFromLocalStorage() {
      const all = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key.startsWith('employeeSurveyResponse_')) {
          try {
            const resp = JSON.parse(localStorage.getItem(key))
            if (resp && typeof resp === 'object') {
              // Extract timestamp from key
              const ts = key.split('_')[1]
              let submissionDateTime = ''
              if (ts && !isNaN(Number(ts))) {
                const date = new Date(Number(ts))
                submissionDateTime = date.toLocaleString()
              }
              all.push({ ...resp, submissionDateTime })
            }
          } catch {}
        }
      }
      // Assign a numeric id (1, 2, 3, ...) for table rendering
      this.responses = all.map((resp, idx) => ({
        ...resp,
        id: idx + 1,
      }))
    },
    async exportToExcel() {
      try {
        // Get all responses for export (not just current page)
        const params = {
          limit: 1000, // Get more data for export
          ...this.filters
        }
        
        const response = await EmployeeSurveyService.getAllResponses(params)
        const allResponses = response.success && response.data ? response.data.responses : this.responses
        
        // 1. Gather all unique questions (with section) as columns
        const questionColumns = [];
        const questionMap = {}; // { 'Section: Question': true }
        allResponses.forEach(resp => {
          Object.values(resp.response || resp.responses).forEach(section => {
            if (section.section === 'Final Feedback') {
              Object.keys(section.responses).forEach(q => {
                const col = `Final Feedback: ${q}`;
                if (!questionMap[col]) {
                  questionColumns.push(col);
                  questionMap[col] = true;
                }
              });
            } else {
              Object.values(section.responses).forEach(q => {
                const col = `${section.section}: ${q.question}`;
                if (!questionMap[col]) {
                  questionColumns.push(col);
                  questionMap[col] = true;
                }
              });
            }
          });
        });

        // 2. Build rows
        const rows = allResponses.map(resp => {
          const row = {
            'Employee ID': resp.emp_id || resp.empId,
            'Status': resp.status,
            'Response ID': resp.responseId || resp.id,
            'Submission Date & Time': resp.created_at ? new Date(resp.created_at).toLocaleString() : resp.submissionDateTime || '',
            'Anonymous': resp.isAnonymous ? 'Yes' : 'No',
          };
          
          // Fill in answers for each question column
          questionColumns.forEach(col => {
            // Try to find the answer for this column in this response
            let found = false;
            Object.values(resp.response || resp.responses).forEach(section => {
              if (section.section === 'Final Feedback') {
                Object.entries(section.responses).forEach(([q, a]) => {
                  const key = `Final Feedback: ${q}`;
                  if (key === col) {
                    row[col] = a;
                    found = true;
                  }
                });
              } else {
                Object.values(section.responses).forEach(qObj => {
                  const key = `${section.section}: ${qObj.question}`;
                  if (key === col) {
                    row[col] = qObj.answer + (qObj.remark ? ` (${qObj.remark})` : '');
                    found = true;
                  }
                });
              }
            });
            if (!found) row[col] = '';
          });
          return row;
        });

        // 3. Export
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'SurveyResponses');
        XLSX.writeFile(wb, 'EmployeeSurveyResponses.xlsx');
        
        this.$bvToast.toast('Export completed successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        this.$bvToast.toast(error.message || 'Failed to export data', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    
    async onPageChange(page) {
      this.pagination.page = page
      await this.loadResponses()
    },
    
    async onFilterChange() {
      this.pagination.page = 1
      await this.loadResponses()
    },
    
    async deleteResponse(id) {
      try {
        const response = await EmployeeSurveyService.deleteResponse(id)
        if (response.success) {
          this.$bvToast.toast('Response deleted successfully!', {
            title: 'Success',
            variant: 'success',
            solid: true,
          })
          await this.loadResponses()
        }
      } catch (error) {
        console.error('Error deleting response:', error)
        this.$bvToast.toast(error.message || 'Failed to delete response', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
  components: {
    BSpinner,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BPagination
  },
  async mounted() {
    await Promise.all([
      this.loadResponses(),
      this.loadStatistics()
    ])
  },
}
</script>

<style scoped>
.employee-survey-responses {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 0 40px 0;
}
.b-card-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}
.stat-card {
  padding: 15px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}
.stat-card h3 {
  margin-bottom: 5px;
  font-weight: bold;
}
.stat-card p {
  color: #6c757d;
  font-size: 0.9rem;
}
.gap-1 {
  gap: 0.25rem;
}
</style> 